import * as React from 'react'
import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import { LoaderCircleIcon } from 'lucide-react'

import { cn } from '@/components/new/lib/utils'

const buttonVariants = cva(
    "group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
        variants: {
            variant: {
                default:
                    'bg-[color:var(--button-primary,var(--primary))] text-[color:var(--button-primary-foreground,var(--primary-foreground))] hover:bg-[color:var(--button-primary,var(--primary))]/80',
                outline:
                    'border-border bg-background shadow-xs hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
                ghost: 'hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground',
                destructive:
                    'bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30',
                link: 'text-foreground underline underline-offset-4 cursor-pointer',
            },
            size: {
                default:
                    'h-9 gap-1.5 px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
                xs: "h-6 gap-1 rounded-[min(var(--radius-md),8px)] px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
                sm: 'h-8 gap-1 rounded-[min(var(--radius-md),10px)] px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
                lg: 'h-10 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
                icon: 'size-9',
                'icon-xs':
                    "size-6 rounded-[min(var(--radius-md),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
                'icon-sm': 'size-8 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-md',
                'icon-lg': 'size-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
)

type ButtonProps = ButtonPrimitive.Props &
    VariantProps<typeof buttonVariants> & {
        loading?: boolean
    }

const wrapButtonLabel = (child: React.ReactNode, keyPrefix: string): React.ReactNode => {
    if (typeof child === 'string' || typeof child === 'number') {
        return (
            <span
                key={keyPrefix}
                data-slot="button-label"
                className="transition-[opacity,visibility] duration-200 group-data-[loading=true]/button:invisible group-data-[loading=true]/button:opacity-0"
            >
                {child}
            </span>
        )
    }

    if (!React.isValidElement(child)) {
        return child
    }

    if (child.type === React.Fragment) {
        return (
            <React.Fragment key={keyPrefix}>
                {React.Children.map(child.props.children, (nestedChild, index) =>
                    wrapButtonLabel(nestedChild, `${keyPrefix}-${index}`)
                )}
            </React.Fragment>
        )
    }

    return child
}

const Button = React.forwardRef<React.ElementRef<typeof ButtonPrimitive>, ButtonProps>(
    ({ children, className, disabled, loading = false, variant = 'default', size = 'default', ...props }, ref) => {
        const [showSpinner, setShowSpinner] = React.useState(false)

        React.useEffect(() => {
            if (!loading) {
                setShowSpinner(false)
                return
            }

            const timeout = window.setTimeout(() => {
                setShowSpinner(true)
            }, 500)

            return () => {
                window.clearTimeout(timeout)
            }
        }, [loading])

        return (
            <ButtonPrimitive
                ref={ref}
                data-slot="button"
                data-loading={showSpinner}
                disabled={disabled || loading}
                className={cn(buttonVariants({ variant, size }), 'relative', className)}
                {...props}
            >
                {React.Children.map(children, (child, index) => wrapButtonLabel(child, `button-child-${index}`))}
                <span
                    aria-hidden
                    className={cn(
                        'pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden'
                    )}
                >
                    <LoaderCircleIcon
                        className={cn(
                            'size-4 translate-y-2 opacity-0 transition-[opacity,transform] duration-200',
                            showSpinner && 'translate-y-0 animate-spin opacity-100'
                        )}
                    />
                </span>
            </ButtonPrimitive>
        )
    }
)

Button.displayName = 'Button'

export { Button, buttonVariants }
